// BookingOccurrenceList — replaces the visual Clearway component family across the booking
// flows and ViewBooking. See `docs/BOOKING_RULES.md` (R5/R6/R7 buffer-overlap logic).
//
// Renders a scrollable list of weekly/monthly occurrences for a proposed (or existing)
// recurring booking. Each row shows the date and an availability badge; unavailable rows
// surface a "Clashes with HH:MM Surname" note that names every conflicting booking on that
// day. Parent passes `onAvailabilityChange` to gate the proceed CTA — per spec, a recurring
// booking cannot proceed unless every one of its occurrences is available.
//
// **Manageable mode** (`manageable={{ booking, onMutated }}`): in addition to the read-only
// list, each row gets per-row actions:
//   • Available → "Exclude" (adds the date to recurrence.exceptions; the slot frees up).
//   • Excluded → "Reinstate" (runs a single-date availability pre-check first; refuses with
//     a toast if the room is no longer free at that time).
// Used on the "Manage series" panel inside PracticeBookingView.
//
// 52-week ceiling: bookings starting partway through the next 52 weeks get the remaining
// window (e.g. a slot starting 17 weeks out gets 35 weekly occurrences, not 52). This keeps
// the list usefully bounded; the underlying recurrence is still indefinite per R13.

import { useEffect, useMemo, useState } from "react";
import { format } from "date-fns";
import { AlertTriangle, BanknoteArrowDown, BanknoteArrowUp, CalendarOff, CheckCircle2, Undo2, XCircle } from "lucide-react";
import { utcDayKey } from "@/lib/calendarDay";
import { OccurrenceWaiveConfirm } from "@/components/tpr/OccurrenceWaiveConfirm/OccurrenceWaiveConfirm";
import { useToggleGratis } from "@/hooks/useToggleGratis";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { RecurrenceExceptionError } from "@/api/remoteBookingApi";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Spinner } from "@/components/ui/spinner";
import { useSetRecurrenceException } from "@/hooks/useSetRecurrenceException";
import { isCancellationChargeable } from "@/utils/cancellationCharge";
import { cn, getAmenityColumnLabel } from "@/lib/utils";
import { useLocationsQuery } from "@/hooks/useLocationsQuery";
import { useOccurrenceAmenities } from "@/hooks/useOccurrenceAmenities";
import type { OccurrenceAmenities } from "@/types/OccurrenceAmenities";
import { OccurrenceAmenitySelectionBar } from "./OccurrenceAmenitySelectionBar";
import { BookingService } from "@/services/BookingService";
import { isFirstWeekOfMonth } from "@/utils/slotPullForward";
import { toast } from "sonner";
import type { Booking } from "@/types/Booking";

const WEEKS_CEILING = 52;
const MS_PER_DAY = 24 * 60 * 60 * 1000;
const MS_PER_WEEK = 7 * MS_PER_DAY;

export interface OccurrenceClash {
  bookingId: string;
  memberLastName: string;
  /** HH:MM */
  startTime: string;
  /** Whether this clash is a core-time overlap (R7, always blocking) or a buffer-only
   *  overlap against a different member (R5). A later task uses this to relax the gate
   *  for elevated users on buffer-only clashes. */
  conflictType: "core_overlap" | "buffer_overlap";
}

export interface BookingOccurrenceListProps {
  roomId: string;
  memberId: string;
  startDate: Date;
  /** HHmm — e.g. "1100" */
  startTime: string;
  durationMinutes: number;
  frequency?: "weekly" | "monthly";
  interval?: number;
  occurrenceCount: number;
  /** Inclusive last date the series runs to (e.g. the owner's membership/termination end). Occurrences
   *  after this day are dropped so the list never shows dates beyond the termination date. */
  seriesEndDate?: Date | null;
  /** Drop past-dated occurrences from the list (used by ViewBooking). Defaults true. */
  futureOnly?: boolean;
  /**
   * Admin / Practice Manager. When true, an occurrence whose clashes are ALL `buffer_overlap`
   * no longer counts as unavailable — it renders as an amber warning row instead of a hard
   * block, and `onAvailabilityChange` reports `bufferOverrideNeeded: true`. An occurrence with
   * any `core_overlap` clash still blocks everyone, elevated or not. Non-elevated callers are
   * unaffected (default false/undefined) — every clash still blocks, as before.
   */
  isElevated?: boolean;
  /** Notifies parent whether every visible occurrence is available. `bufferOverrideNeeded` is
   *  true when at least one active occurrence was only let through (for an elevated user)
   *  because its clashes were buffer-only — the parent may want to warn before proceeding. */
  onAvailabilityChange?: (
    allAvailable: boolean,
    totals: { available: number; total: number; bufferOverrideNeeded: boolean },
  ) => void;
  /** Skip the network round-trip — useful in tests. Keys are ISO dates (YYYY-MM-DD). */
  preloadedClashes?: Map<string, OccurrenceClash[]>;
  /** Inject a fake clock for tests. */
  now?: Date;
  /**
   * Slot create flow only. When set, occurrences before this date are de-emphasised and excluded
   * from the availability gate, and the matching row is marked "Your slot starts here".
   * Not intended for use together with `manageable` mode.
   */
  slotStartDate?: Date;
  /**
   * Slot create flow only. When provided, dimmed (pre-start) AVAILABLE rows get a "Book Flex"
   * button that creates a single adhoc booking for that date. Resolves with the created booking's
   * shortCode (reference); rejects on failure. Not intended for use with `manageable` mode.
   */
  onBookFlex?: (occurrenceDate: Date) => Promise<string>;
  /**
   * Slot create flow only (R24 pull-forward exception). When provided, each pre-start (gap) row
   * gets a "Start Slot Here" button that pulls the slot's start forward to that date. Disabled when
   * the row is unavailable, or once any flex booking has been made in this window (flex and
   * pull-forward are mutually exclusive). Not intended for use with `manageable` mode.
   */
  onStartSlotHere?: (occurrenceDate: Date) => void;
  /**
   * R24a role-aware minimum notice. When provided (with `onStartSlotHere`), pre-start rows whose
   * date is BEFORE this earliest date are too soon for the current user's role — their "Start Slot
   * Here" button is disabled with a "needs more notice" hint. A PM's earliest is today + 7 days; an
   * admin's is tomorrow. The server re-validates regardless (`validateSlotStartOverride`).
   */
  earliestSlotStartDate?: Date;
  /**
   * R24a — when true (practice managers), the "Start Slot Here" button is shown ONLY on pre-start
   * rows that fall in the first week of a month (day-of-month 1–7); weeks 2–4 are hidden entirely.
   * Admins are unrestricted (leave false/undefined). The server re-validates
   * (`validateSlotStartOverride` with `restrictToFirstWeekOfMonth`).
   */
  firstWeekOfMonthOnly?: boolean;
  /**
   * Turn on per-row Exclude / Reinstate actions. The component reads existing exceptions from
   * the booking's recurrence (or recurrencePattern) and handles the mutation + pre-flight
   * availability check internally. Parent receives `onMutated` after each successful change so
   * it can refetch.
   */
  manageable?: {
    booking: Booking;
    onMutated?: () => void;
    /**
     * Enable the per-row Waive fee / Restore charge action. Financial and staff-only, so it is
     * gated separately from the (schedule-only) Exclude/Reinstate actions that org owners may also
     * use — the caller passes the same capability the whole-series waive button uses
     * (`canManageLocationCharges`). Off by default.
     */
    canWaive?: boolean;
    /**
     * R27 — staff-only per-occurrence amenity controls: per-date amenity chips, a Custom badge,
     * row checkboxes and the "Change amenities / Reset to series default" bar.
     */
    canManageAmenities?: boolean;
  };
}

interface OccurrenceRow {
  date: Date;
  iso: string;
  clashes: OccurrenceClash[];
  excluded: boolean;
  preStart: boolean;
}

type FlexStatus = "idle" | "loading" | "booked" | "error";
interface FlexState {
  status: FlexStatus;
  shortCode?: string;
}

interface PendingAction {
  iso: string;
  date: Date;
  action: "exclude" | "reinstate" | "waive" | "restore";
}

const formatClashList = (clashes: OccurrenceClash[]): string => {
  const labels = clashes.map((c) => `${c.startTime} ${c.memberLastName}`);
  if (labels.length === 0) return "";
  if (labels.length === 1) return labels[0];
  if (labels.length === 2) return `${labels[0]} and ${labels[1]}`;
  const head = labels.slice(0, -1).join(", ");
  return `${head} and ${labels[labels.length - 1]}`;
};

export const enumerateOccurrences = (
  startDate: Date,
  occurrenceCount: number,
  frequency: "weekly" | "monthly",
  interval: number,
  now: Date,
  // Inclusive last date the series runs to (e.g. a member's termination end). Occurrences after this
  // calendar day are dropped so the "Manage series" list never shows dates beyond termination.
  seriesEndDate?: Date | null,
): Date[] => {
  const ceiling = new Date(now.getTime() + WEEKS_CEILING * MS_PER_WEEK);
  // Compare by UTC calendar day (UTC midnight) so a time-of-day on either side never flips a
  // boundary, and so the boundary agrees with occursOnDate / the rest of the UTC-anchored
  // calendar — a booking's stored `date` is UTC-midnight of the centre's day (see @/lib/calendarDay).
  const endDay = seriesEndDate
    ? Date.UTC(seriesEndDate.getUTCFullYear(), seriesEndDate.getUTCMonth(), seriesEndDate.getUTCDate())
    : null;
  const pastEnd = (target: Date): boolean =>
    endDay !== null && Date.UTC(target.getUTCFullYear(), target.getUTCMonth(), target.getUTCDate()) > endDay;
  const dates: Date[] = [];

  if (frequency === "monthly") {
    const day = startDate.getUTCDate();
    for (let i = 0; i < occurrenceCount; i++) {
      const target = new Date(Date.UTC(startDate.getUTCFullYear(), startDate.getUTCMonth() + i * interval, day));
      if (target.getUTCDate() !== day) continue;
      if (target.getTime() > ceiling.getTime()) break;
      if (pastEnd(target)) break;
      dates.push(target);
    }
    return dates;
  }

  for (let i = 0; i < occurrenceCount; i++) {
    // DST-safe week stepping: build each occurrence from UTC calendar fields (UTC midnight) rather
    // than adding a fixed 7*24h of milliseconds or using local-time getters/constructors. UTC dates
    // have no DST transitions, so `Date.UTC(y, m, d + N)` always rolls month/year over correctly and
    // lands on the same UTC weekday — and, unlike local-time stepping, it never disagrees with the
    // UTC-anchored `occursOnDate` used to detect clashes against these occurrence dates (see
    // src/lib/recurrence.ts / @/lib/calendarDay). Local-time stepping drifted a day in positive-offset
    // timezones and, separately, drifted an hour across the autumn BST→GMT clock change.
    const target = new Date(
      Date.UTC(startDate.getUTCFullYear(), startDate.getUTCMonth(), startDate.getUTCDate() + i * interval * 7),
    );
    if (target.getTime() > ceiling.getTime()) break;
    if (pastEnd(target)) break;
    dates.push(target);
  }
  return dates;
};

/** UTC calendar-day key ("yyyy-MM-dd") — matches occurrence dates, which are UTC-midnight (see
 *  enumerateOccurrences), and the UTC-anchored day math the rest of the calendar uses. */
export const toIsoDate = (d: Date): string => {
  const yyyy = d.getUTCFullYear();
  const mm = String(d.getUTCMonth() + 1).padStart(2, "0");
  const dd = String(d.getUTCDate()).padStart(2, "0");
  return `${yyyy}-${mm}-${dd}`;
};

/**
 * Build the set of excluded-occurrence keys from a recurrence's `exceptions`.
 *
 * Exceptions reach us in more than one shape: the slot-holiday and per-occurrence-exclusion flows
 * store a `yyyy-MM-dd` string, but imported / legacy bookings can carry a full ISO datetime (and a
 * few paths hand us a `Date`). Occurrence rows are keyed by `toIsoDate(date)`, so every exception
 * must be normalised to that same local `yyyy-MM-dd` key — otherwise a stored holiday silently fails
 * the `has()` check and the date renders as "Available" instead of "Excluded" (the PM/admin bug where
 * a member's slot holiday didn't show as excluded on the booking's occurrence list).
 */
export const buildExcludedDateSet = (exceptions?: (string | Date)[] | null): Set<string> =>
  new Set<string>((exceptions ?? []).map((d) => toIsoDate(new Date(d))));

const startOfDay = (d: Date): Date => {
  const c = new Date(d);
  c.setHours(0, 0, 0, 0);
  return c;
};

/**
 * Hook-bearing sub-component that owns the exception mutation + confirm dialog. Extracted so
 * the parent BookingOccurrenceList only mounts (and consequently invokes) the React Query
 * hook when manageable mode is active — the read-only path used by the create flows stays
 * QueryClient-free for tests that don't need it.
 */
const ManageableActions = ({
  booking,
  pendingAction,
  onClose,
  onMutated,
}: {
  booking: Booking;
  pendingAction: PendingAction | null;
  onClose: () => void;
  onMutated?: () => void;
}) => {
  const exceptionMutation = useSetRecurrenceException();
  const toggleGratis = useToggleGratis();
  const [waive, setWaive] = useState(false);

  // C1 — excluding an occurrence within 24h of its start is charged like a late cancellation (the
  // backend writes a Cancellation record). Warn the manager before confirming. Manageable mode is
  // staff-only (PracticeBookingView), so they may waive. Only the "exclude" direction can be charged.
  const willBeCharged =
    pendingAction?.action === "exclude" &&
    booking.type !== "slot" && // slots never accrue cancellation charges (C11)
    isCancellationChargeable(booking, { fromDate: pendingAction.date }).chargeable;

  // Reset the waive choice whenever the pending action changes (open a different row, or close).
  useEffect(() => {
    setWaive(false);
  }, [pendingAction?.iso, pendingAction?.action]);

  const handleConfirm = async () => {
    if (!pendingAction) return;
    try {
      await exceptionMutation.mutateAsync({
        bookingId: booking.id,
        date: pendingAction.iso,
        action: pendingAction.action === "exclude" ? "add" : "remove",
        waive: pendingAction.action === "exclude" && waive,
      });
      toast.success(
        pendingAction.action === "exclude"
          ? `Excluded ${format(pendingAction.date, "EEEE d MMMM")}`
          : `Reinstated ${format(pendingAction.date, "EEEE d MMMM")}`,
      );
      onMutated?.();
    } catch (err) {
      if (err instanceof RecurrenceExceptionError && err.reason === "amenity_clash") {
        toast.error(
          `Cannot reinstate ${format(pendingAction.date, "EEEE d MMMM")}: an attached amenity is already booked at that time. Free the amenity first or use a different date.`,
        );
      } else if (err instanceof RecurrenceExceptionError && err.reason === "clash") {
        toast.error(
          `Cannot reinstate ${format(pendingAction.date, "EEEE d MMMM")}: the room is no longer free at that time.`,
        );
      } else {
        toast.error(err instanceof Error ? err.message : "Could not update the exception. Please try again.");
      }
    } finally {
      onClose();
    }
  };

  // Waiving / restoring a single occurrence's charge. Distinct from the exclude "waive the
  // cancellation charge" toggle above: this writes the occurrence into booking.waivedOccurrences
  // (or clears it) via the gratis endpoint, keyed on occurrenceDate.
  const handleWaiveConfirm = async (reason?: string) => {
    if (!pendingAction) return;
    try {
      // useToggleGratis surfaces its own success/error toast, so we only drive the flow here.
      await toggleGratis.mutateAsync({
        bookingId: booking.id,
        gratis: pendingAction.action === "waive",
        occurrenceDate: pendingAction.iso,
        reason,
      });
      onMutated?.();
    } catch {
      // Error toast handled by the hook; swallow to avoid an unhandled rejection.
    } finally {
      onClose();
    }
  };

  if (!pendingAction) return null;

  if (pendingAction.action === "waive" || pendingAction.action === "restore") {
    return (
      <OccurrenceWaiveConfirm
        mode={pendingAction.action}
        date={pendingAction.date}
        isPending={toggleGratis.isPending}
        onConfirm={handleWaiveConfirm}
        onClose={onClose}
      />
    );
  }

  return (
    <AlertDialog
      open={pendingAction !== null}
      onOpenChange={(open) => {
        if (!open && !exceptionMutation.isPending) onClose();
      }}
    >
      <AlertDialogContent data-testid="occurrence-exception-confirm">
        <AlertDialogHeader>
          <AlertDialogTitle>
            {pendingAction.action === "exclude" ? "Exclude this date?" : "Reinstate this date?"}
          </AlertDialogTitle>
          <AlertDialogDescription>
            {pendingAction.action === "exclude"
              ? `${format(pendingAction.date, "EEEE d MMMM yyyy")} will no longer be booked. The room (and any attached amenities) frees up for that date.`
              : `${format(pendingAction.date, "EEEE d MMMM yyyy")} will be re-added to this series.`}
          </AlertDialogDescription>
        </AlertDialogHeader>
        {willBeCharged && (
          <div
            data-testid="occurrence-exclusion-charge-warning"
            className="flex gap-2 rounded-lg border-2 border-amber-400 bg-amber-50 p-3"
          >
            <AlertTriangle className="mt-0.5 size-5 flex-shrink-0 text-amber-600" />
            <div>
              <h3 className="mb-1 text-sm font-semibold text-amber-900">Cancellation charges apply</h3>
              <p className="text-xs text-amber-800">
                This occurrence starts within 24 hours, so excluding it charges the member — the same
                as a late cancellation.
              </p>
              <label className="mt-2 flex items-center gap-2 text-xs font-medium text-amber-900">
                <Checkbox
                  data-testid="occurrence-exclusion-waive-toggle"
                  checked={waive}
                  onCheckedChange={(v) => setWaive(v === true)}
                />
                Waive this charge
              </label>
            </div>
          </div>
        )}
        <AlertDialogFooter>
          <AlertDialogCancel disabled={exceptionMutation.isPending}>Cancel</AlertDialogCancel>
          <AlertDialogAction
            onClick={handleConfirm}
            disabled={exceptionMutation.isPending}
            data-testid="occurrence-exception-confirm-action"
          >
            {exceptionMutation.isPending ? "Saving…" : pendingAction.action === "exclude" ? "Exclude" : "Reinstate"}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
};

const EMPTY_ENTRIES = new Map<string, OccurrenceAmenities>();
const EMPTY_NAMES = new Map<string, string>();

interface AmenityData {
  entries: Map<string, OccurrenceAmenities>;
  nameById: Map<string, string>;
}

const BookingOccurrenceListInner = ({
  roomId,
  memberId,
  startDate,
  startTime,
  durationMinutes,
  frequency = "weekly",
  interval = 1,
  occurrenceCount,
  seriesEndDate,
  futureOnly = true,
  onAvailabilityChange,
  preloadedClashes,
  now,
  slotStartDate,
  onBookFlex,
  onStartSlotHere,
  earliestSlotStartDate,
  firstWeekOfMonthOnly,
  manageable,
  isElevated,
  amenityData,
}: BookingOccurrenceListProps & { amenityData?: AmenityData }) => {
  const effectiveNow = now ?? new Date();
  const [clashes, setClashes] = useState<Map<string, OccurrenceClash[]>>(
    preloadedClashes ?? new Map(),
  );
  const [isLoading, setIsLoading] = useState(!preloadedClashes);
  const [pendingAction, setPendingAction] = useState<PendingAction | null>(null);
  const [checkingReinstate, setCheckingReinstate] = useState<string | null>(null);
  const [flexStates, setFlexStates] = useState<Map<string, FlexState>>(new Map());

  // R27 — per-occurrence amenities (staff only); data arrives from BookingOccurrenceList's wrapper.
  const amenitiesEnabled = !!amenityData;
  const amenityEntries = amenityData?.entries ?? EMPTY_ENTRIES;
  const amenityNameById = amenityData?.nameById ?? EMPTY_NAMES;
  const [selectedAmenityDates, setSelectedAmenityDates] = useState<Set<string>>(new Set());
  const toggleAmenityDate = (iso: string) =>
    setSelectedAmenityDates((cur) => {
      const next = new Set(cur);
      if (next.has(iso)) next.delete(iso);
      else next.add(iso);
      return next;
    });

  // Set of ISO dates currently excluded from the recurrence (read from manageable.booking).
  const excludedSet = useMemo(() => {
    if (!manageable) return new Set<string>();
    const rec = manageable.booking.recurrence ?? manageable.booking.recurrencePattern;
    return buildExcludedDateSet(rec?.exceptions);
  }, [manageable]);

  // Set of ISO dates with an explicit per-occurrence waive, and whether the WHOLE series is gratis.
  // A series waive covers every occurrence and cannot be lifted for just one, so those rows show a
  // static indicator; per-occurrence waives are individually restorable. Normalise the stored dates
  // to the same UTC day-key the rows use (waivedOccurrences may carry full ISO datetimes).
  const waivedSet = useMemo(() => {
    if (!manageable) return new Set<string>();
    return new Set((manageable.booking.waivedOccurrences ?? []).map((w) => toIsoDate(new Date(w.date))));
  }, [manageable]);
  const seriesWaived = !!manageable?.booking.gratis;

  const slotStartIso = useMemo(
    () => (slotStartDate ? toIsoDate(slotStartDate) : null),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [slotStartDate?.getTime()],
  );

  // R24 pull-forward — once any flex booking exists in this window, "Start Slot Here" is disabled
  // (flex and pull-forward are mutually exclusive in a session).
  const anyFlexBooked = useMemo(
    () => Array.from(flexStates.values()).some((f) => f.status === "booked"),
    [flexStates],
  );

  const occurrences = useMemo<OccurrenceRow[]>(() => {
    const dates = enumerateOccurrences(startDate, occurrenceCount, frequency, interval, effectiveNow, seriesEndDate);
    // Compare by UTC calendar day, not the local `startOfDay` helper above (kept for the unrelated
    // earliestSlotStartDate comparison further down). Occurrence dates are UTC-midnight (see
    // enumerateOccurrences), so a local floor of `effectiveNow` reads "today" as the wrong calendar
    // day for a viewer west of UTC near midnight, silently dropping today's occurrence — the
    // travelling-member bug (see @/lib/calendarDay).
    const todayIso = utcDayKey(effectiveNow);
    const filtered = futureOnly ? dates.filter((d) => utcDayKey(d) >= todayIso) : dates;
    return filtered.map((d) => {
      const iso = toIsoDate(d);
      const rowClashes = clashes.get(iso) ?? [];
      // Sort defensively so "Clashes with HH:MM Surname, HH:MM Surname, …" wording always reads
      // ascending by time — the service already sorts on the network path; this protects the
      // preloaded-test path and any future caller that hands in unsorted data.
      const sorted = [...rowClashes].sort((a, b) => a.startTime.localeCompare(b.startTime));
      return {
        date: d,
        iso,
        clashes: sorted,
        excluded: excludedSet.has(iso),
        preStart: slotStartIso !== null && iso < slotStartIso,
      };
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [startDate.getTime(), occurrenceCount, frequency, interval, futureOnly, clashes, excludedSet, slotStartIso, seriesEndDate?.getTime()]);

  useEffect(() => {
    if (preloadedClashes) return;
    if (!roomId || !memberId || !startDate) return;
    let cancelled = false;
    setIsLoading(true);
    const bookingService = new BookingService();
    bookingService
      .checkRecurringAvailability(
        roomId,
        startDate,
        startTime,
        durationMinutes,
        occurrenceCount,
        memberId,
        frequency,
        interval,
        // In manageable mode the booking itself sits on every one of its own occurrence
        // dates — exclude it so the panel doesn't report every row as "clashes with itself".
        manageable?.booking.id,
      )
      .then((result) => {
        if (cancelled) return;
        setClashes(result.clashesByDateIso);
        setIsLoading(false);
      })
      .catch(() => {
        if (cancelled) return;
        setClashes(new Map());
        setIsLoading(false);
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [roomId, memberId, startDate.getTime(), startTime, durationMinutes, frequency, interval, occurrenceCount, manageable?.booking.id]);

  // For availability totals, an excluded row counts as "neither" (it's intentionally not
  // happening). We only count rows that would actually run: non-excluded, non-pre-start.
  //
  // Elevated-aware gating (R5/R7): a buffer-only clash (all `buffer_overlap`, no `core_overlap`)
  // no longer counts as blocking for an elevated user — it's surfaced instead via
  // `bufferOverrideNeeded` so the parent can warn / require an explicit override. Any
  // `core_overlap` clash still blocks everyone. Non-elevated callers see identical behaviour to
  // before this change: any clash (buffer or core) blocks.
  const totals = useMemo(() => {
    const active = occurrences.filter((o) => !o.excluded && !o.preStart);
    const total = active.length;
    const isBlocking = (o: OccurrenceRow) => {
      const hasCoreClash = o.clashes.some((c) => c.conflictType === "core_overlap");
      const hasBufferClash = o.clashes.some((c) => c.conflictType === "buffer_overlap");
      return hasCoreClash || (!isElevated && hasBufferClash);
    };
    const isBufferWarn = (o: OccurrenceRow) => {
      const hasCoreClash = o.clashes.some((c) => c.conflictType === "core_overlap");
      const hasBufferClash = o.clashes.some((c) => c.conflictType === "buffer_overlap");
      return !!isElevated && !hasCoreClash && hasBufferClash;
    };
    const unavailable = active.filter(isBlocking).length;
    const bufferOverrideNeeded = active.some(isBufferWarn);
    return { total, available: total - unavailable, unavailable, bufferOverrideNeeded };
  }, [occurrences, isElevated]);

  const allAvailable = totals.unavailable === 0 && totals.total > 0;

  useEffect(() => {
    onAvailabilityChange?.(allAvailable, {
      available: totals.available,
      total: totals.total,
      bufferOverrideNeeded: totals.bufferOverrideNeeded,
    });
  }, [allAvailable, totals.available, totals.total, totals.bufferOverrideNeeded, onAvailabilityChange]);

  // ---- Manageable-mode handlers ---------------------------------------------------------

  const handleExcludeClick = (row: OccurrenceRow) => {
    setPendingAction({ iso: row.iso, date: row.date, action: "exclude" });
  };

  const handleWaiveClick = (row: OccurrenceRow) => {
    setPendingAction({ iso: row.iso, date: row.date, action: "waive" });
  };

  const handleRestoreWaiveClick = (row: OccurrenceRow) => {
    setPendingAction({ iso: row.iso, date: row.date, action: "restore" });
  };

  const handleReinstateClick = async (row: OccurrenceRow) => {
    if (!manageable) return;
    setCheckingReinstate(row.iso);
    try {
      const bookingService = new BookingService();
      const reinstateClashes = await bookingService.checkSingleDateAvailability(
        roomId,
        row.date,
        startTime,
        durationMinutes,
        memberId,
        manageable.booking.locationId,
      );
      if (reinstateClashes.length > 0) {
        toast.error(
          `Cannot reinstate ${format(row.date, "EEEE d MMMM")}: ${formatClashList(reinstateClashes)} now occupies the room at that time.`,
        );
        return;
      }
      setPendingAction({ iso: row.iso, date: row.date, action: "reinstate" });
    } catch {
      toast.error("Could not check availability. Please try again.");
    } finally {
      setCheckingReinstate(null);
    }
  };

  const handleBookFlex = async (row: OccurrenceRow) => {
    if (!onBookFlex) return;
    const current = flexStates.get(row.iso);
    if (current?.status === "loading" || current?.status === "booked") return;
    setFlexStates((prev) => new Map(prev).set(row.iso, { status: "loading" }));
    try {
      const shortCode = await onBookFlex(row.date);
      setFlexStates((prev) => new Map(prev).set(row.iso, { status: "booked", shortCode }));
    } catch {
      setFlexStates((prev) => new Map(prev).set(row.iso, { status: "error" }));
    }
  };

  return (
    <div className="w-full" data-testid="booking-occurrence-list">
      <div
        className={cn(
          "mb-2 flex items-center justify-between rounded-md border px-3 py-2 text-sm font-medium",
          allAvailable
            ? "border-emerald-200 bg-emerald-50 text-emerald-900"
            : totals.total === 0
              ? "border-gray-200 bg-gray-50 text-gray-700"
              : "border-red-200 bg-red-50 text-red-900",
        )}
        data-testid="booking-occurrence-summary"
      >
        <span>
          {totals.total === 0
            ? "No occurrences to check"
            : `${totals.available} of ${totals.total} occurrence${totals.total === 1 ? "" : "s"} available`}
        </span>
        {isLoading && <Spinner className="h-4 w-4" />}
      </div>

      <div
        className="max-h-[60vh] w-full overflow-y-auto rounded-md border border-gray-200 bg-white"
        data-testid="booking-occurrence-scroll"
      >
        {occurrences.length === 0 ? (
          <p className="p-4 text-sm text-gray-500">No occurrences within the next 52 weeks.</p>
        ) : (
          <ul className="divide-y divide-gray-100" data-testid="booking-occurrence-rows">
            {occurrences.map((occ) => {
              const hasCoreClash = occ.clashes.some((c) => c.conflictType === "core_overlap");
              const hasBufferClash = occ.clashes.some((c) => c.conflictType === "buffer_overlap");
              // isBlocking mirrors the totals calc above: core clashes always block; buffer-only
              // clashes only block a non-elevated user (elevated behaviour is unchanged for
              // core clashes and for anyone without isElevated set).
              const isUnavailable = hasCoreClash || (!isElevated && hasBufferClash);
              // Elevated + buffer-only clash — renders as a warning row, not a hard block.
              const isBufferWarn = !!isElevated && !hasCoreClash && hasBufferClash;
              // Book Flex creates a real, separate adhoc booking via createNewBooking, which never
              // sets overrideBufferConflict — so it has no way to get past the backend's buffer
              // block. Unlike the totals gate / warning row / Start Slot Here / Exclude (all of
              // which are either local-state-only or don't create a new booking), Book Flex must
              // stay hard-gated on ANY clash for every user, elevated or not, or an elevated user
              // hits a guaranteed "Error. Try again" dead-end on a buffer-only row.
              const hasAnyClash = occ.clashes.length > 0;
              const isExcluded = occ.excluded;
              const isCheckingThis = checkingReinstate === occ.iso;
              const isPreStart = occ.preStart;
              const isSlotStart = slotStartIso !== null && occ.iso === slotStartIso;
              const flex =
                onBookFlex && isPreStart && !isExcluded && !hasAnyClash
                  ? (flexStates.get(occ.iso) ?? { status: "idle" as const })
                  : null;
              return (
                <li
                  key={occ.iso}
                  data-testid={`occurrence-row-${occ.iso}`}
                  data-state={
                    isPreStart
                      ? "pre-start"
                      : isExcluded
                        ? "excluded"
                        : isUnavailable
                          ? "unavailable"
                          : isBufferWarn
                            ? "warning"
                            : "available"
                  }
                  className={cn(
                    "flex items-start justify-between gap-4 px-4 py-3",
                    isSlotStart && "border-t border-emerald-200",
                  )}
                >
                  <div className={cn("flex flex-col", isPreStart && "opacity-60")}>
                    <span className="flex items-center gap-2">
                      {amenitiesEnabled && amenityEntries.has(occ.iso) && !isExcluded && (
                        <Checkbox
                          data-testid={`occurrence-select-${occ.iso}`}
                          aria-label={`Select ${format(occ.date, "d MMMM yyyy")}`}
                          checked={selectedAmenityDates.has(occ.iso)}
                          onCheckedChange={() => toggleAmenityDate(occ.iso)}
                        />
                      )}
                      <span className={cn("text-sm text-gray-900", isExcluded && "line-through text-gray-500")}>
                        {format(occ.date, "EEEE d MMMM yyyy")}
                        <span className="ml-1 text-gray-500">(at {startTime.slice(0, 2)}:{startTime.slice(2, 4)})</span>
                      </span>
                    </span>
                    {isSlotStart && (
                      <span
                        data-testid={`occurrence-slot-start-${occ.iso}`}
                        className="mt-0.5 inline-flex w-fit items-center rounded-full bg-emerald-100 px-2 py-0.5 text-xs font-medium text-emerald-800"
                      >
                        Your slot starts here
                      </span>
                    )}
                    {!isExcluded && (isUnavailable || isBufferWarn) && (
                      <span
                        className={cn("mt-0.5 text-xs", isBufferWarn ? "text-amber-700" : "text-red-700")}
                        data-testid={`occurrence-clashes-${occ.iso}`}
                      >
                        Clashes with {formatClashList(occ.clashes)}
                      </span>
                    )}
                    {amenitiesEnabled && !isExcluded && amenityEntries.has(occ.iso) && (
                      <span className="mt-0.5 flex flex-wrap items-center gap-1 text-xs" data-testid={`occurrence-amenities-${occ.iso}`}>
                        {amenityEntries.get(occ.iso)!.amenityIds.length === 0 ? (
                          <span className="text-gray-500">No amenities</span>
                        ) : (
                          amenityEntries.get(occ.iso)!.amenityIds.map((id) => (
                            <span key={id} className="rounded bg-purple-100 px-1.5 py-0.5 font-medium text-purple-800" title={amenityNameById.get(id) ?? id}>
                              {getAmenityColumnLabel(amenityNameById.get(id) ?? id)}
                            </span>
                          ))
                        )}
                        {amenityEntries.get(occ.iso)!.customised && (
                          <span className="rounded-full bg-indigo-100 px-2 py-0.5 font-medium text-indigo-800" data-testid={`occurrence-custom-${occ.iso}`}>
                            Custom
                          </span>
                        )}
                      </span>
                    )}
                  </div>
                  <span className="flex items-center gap-2">
                    <span className={cn("flex items-center gap-2", isPreStart && "opacity-60")}>
                      {isExcluded ? (
                        <>
                          <CalendarOff className="h-4 w-4 text-amber-700" />
                          <span className="rounded-full bg-amber-100 px-2 py-0.5 text-xs font-medium text-amber-800">
                            Excluded
                          </span>
                        </>
                      ) : isUnavailable ? (
                        <>
                          <XCircle className="h-4 w-4 text-red-700" />
                          <span className="rounded-full bg-red-100 px-2 py-0.5 text-xs font-medium text-red-800">
                            Unavailable
                          </span>
                        </>
                      ) : isBufferWarn ? (
                        <>
                          <AlertTriangle className="h-4 w-4 text-amber-700" />
                          <span
                            className="rounded-full bg-amber-100 px-2 py-0.5 text-xs font-medium text-amber-800"
                            data-testid={`occurrence-buffer-warning-${occ.iso}`}
                          >
                            Buffer clash — override required
                          </span>
                        </>
                      ) : (
                        <>
                          <CheckCircle2 className="h-4 w-4 text-emerald-700" />
                          <span className="rounded-full bg-emerald-100 px-2 py-0.5 text-xs font-medium text-emerald-800">
                            Available
                          </span>
                        </>
                      )}
                      {manageable && isExcluded && (
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          onClick={() => handleReinstateClick(occ)}
                          disabled={isCheckingThis}
                          data-testid={`reinstate-${occ.iso}`}
                        >
                          {isCheckingThis ? (
                            <Spinner className="h-3 w-3" />
                          ) : (
                            <>
                              <Undo2 className="h-3 w-3 mr-1" /> Reinstate
                            </>
                          )}
                        </Button>
                      )}
                      {manageable && !isExcluded && !isUnavailable && (
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          onClick={() => handleExcludeClick(occ)}
                          data-testid={`exclude-${occ.iso}`}
                        >
                          <CalendarOff className="h-3 w-3 mr-1" /> Exclude
                        </Button>
                      )}
                      {/* Per-occurrence charge control (staff-only — gated by manageable.canWaive). A
                          whole-series gratis waive covers every occurrence and can't be lifted for
                          one, so those rows get a static indicator; an explicit per-occurrence waive
                          is restorable. */}
                      {manageable?.canWaive && !isExcluded && (
                        seriesWaived ? (
                          <span
                            className="rounded-full bg-emerald-100 px-2 py-0.5 text-xs font-medium text-emerald-800"
                            data-testid={`series-waived-${occ.iso}`}
                            title="Waived as part of the whole series"
                          >
                            Waived (series)
                          </span>
                        ) : waivedSet.has(occ.iso) ? (
                          <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            onClick={() => handleRestoreWaiveClick(occ)}
                            data-testid={`waive-restore-${occ.iso}`}
                          >
                            <BanknoteArrowUp className="h-3 w-3 mr-1" /> Restore charge
                          </Button>
                        ) : (
                          <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            onClick={() => handleWaiveClick(occ)}
                            data-testid={`waive-${occ.iso}`}
                          >
                            <BanknoteArrowDown className="h-3 w-3 mr-1" /> Waive fee
                          </Button>
                        )
                      )}
                    </span>
                    {flex &&
                      (flex.status === "booked" ? (
                        <>
                          <span
                            data-testid={`book-flex-ref-${occ.iso}`}
                            className="font-mono text-xs text-gray-700"
                          >
                            {flex.shortCode}
                          </span>
                          <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            disabled
                            data-testid={`book-flex-${occ.iso}`}
                          >
                            Booked
                          </Button>
                        </>
                      ) : (
                        <>
                          {flex.status === "error" && (
                            <span
                              data-testid={`book-flex-error-${occ.iso}`}
                              className="text-xs text-red-700"
                            >
                              Error. Try again
                            </span>
                          )}
                          <Button
                            type="button"
                            size="sm"
                            className="bg-black text-white hover:bg-black/90"
                            disabled={flex.status === "loading"}
                            onClick={() => handleBookFlex(occ)}
                            data-testid={`book-flex-${occ.iso}`}
                          >
                            {flex.status === "loading" ? <Spinner className="h-3 w-3" /> : "Book Flex"}
                          </Button>
                        </>
                      ))}
                    {/* R24 pull-forward — admin/PM pulls the slot start to this gap date. Disabled
                        when the row is unavailable, once any flex has been booked in this window, or
                        when the date is sooner than the role's minimum notice (PM 7 days / admin
                        tomorrow). R24a: practice managers may only start in the first week of a month,
                        so weeks 2–4 of the gap have no button at all (firstWeekOfMonthOnly). */}
                    {onStartSlotHere &&
                      isPreStart &&
                      !isExcluded &&
                      (!firstWeekOfMonthOnly || isFirstWeekOfMonth(occ.date)) && (
                      (() => {
                        // Compare by calendar day, not raw instant — occurrence dates are local
                        // midnight and `earliestSlotStartDate` may be anchored differently, so a raw
                        // getTime() compare can be off by an hour (and thus a day) at tz boundaries.
                        const isTooSoon =
                          !!earliestSlotStartDate &&
                          startOfDay(occ.date).getTime() < startOfDay(earliestSlotStartDate).getTime();
                        return (
                          <span className="flex items-center gap-2">
                            <Button
                              type="button"
                              variant="outline"
                              size="sm"
                              disabled={isUnavailable || anyFlexBooked || isTooSoon}
                              onClick={() => onStartSlotHere(occ.date)}
                              data-testid={`start-slot-here-${occ.iso}`}
                            >
                              Start Slot Here
                            </Button>
                            {isTooSoon && !isUnavailable && !anyFlexBooked && (
                              <span
                                className="text-xs text-gray-500"
                                data-testid={`start-slot-too-soon-${occ.iso}`}
                              >
                                Needs more notice
                              </span>
                            )}
                          </span>
                        );
                      })()
                    )}
                  </span>
                </li>
              );
            })}
          </ul>
        )}
      </div>

      {manageable && amenitiesEnabled && selectedAmenityDates.size > 0 && (
        <OccurrenceAmenitySelectionBar
          booking={manageable.booking}
          selectedDates={[...selectedAmenityDates]}
          entriesByDate={amenityEntries}
          onDone={() => {
            setSelectedAmenityDates(new Set());
            manageable.onMutated?.();
          }}
        />
      )}

      {manageable && (
        <ManageableActions
          booking={manageable.booking}
          pendingAction={pendingAction}
          onClose={() => setPendingAction(null)}
          onMutated={manageable.onMutated}
        />
      )}
    </div>
  );
};

/**
 * R27 — the amenity queries live in a wrapper that only mounts for staff, so lists that never
 * show amenity controls make no amenity requests and need no query client.
 */
const BookingOccurrenceListWithAmenities = (props: BookingOccurrenceListProps) => {
  const booking = props.manageable!.booking;
  const { data: occurrenceAmenities } = useOccurrenceAmenities(booking.id, true);
  const { data: amenityLocations } = useLocationsQuery({
    filters: { locationId: booking.locationId },
    enabled: true,
  });
  const entries = useMemo(() => new Map((occurrenceAmenities ?? []).map((e) => [e.date, e])), [occurrenceAmenities]);
  const nameById = useMemo(() => {
    const loc = amenityLocations?.find((l) => l.id === booking.locationId);
    return new Map((loc?.amenities ?? []).map((a) => [a.id, a.name]));
  }, [amenityLocations, booking.locationId]);
  return <BookingOccurrenceListInner {...props} amenityData={{ entries, nameById }} />;
};

export const BookingOccurrenceList = (props: BookingOccurrenceListProps) =>
  props.manageable?.canManageAmenities ? <BookingOccurrenceListWithAmenities {...props} /> : <BookingOccurrenceListInner {...props} />;

export default BookingOccurrenceList;
